import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import type { DailyRouteActivity, DailyRouteStop } from '@apis/reports';
import { APP_DEFAULT_TZ, formatDuration, formatTimeRange } from '@utils/dateUtils';
import { ActivitiesSection } from '../../shared/ActivitiesSection';
import { EquipmentSection } from '../../shared/EquipmentSection';
import { PersonnelSection } from '../../shared/PersonnelSection';
import styles from './StopDetail.module.css';

interface ActivitiesQuery {
  data: DailyRouteActivity[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

interface IStopDetailProps {
  stop: DailyRouteStop;
  activities: ActivitiesQuery;
}

/**
 * Middle panel of the Daily Route report. Header is structured as Trusstor's
 * LocationHeader: a small uppercase "LOCATION N" eyebrow above the floor name,
 * with the duration top-right; the area code + arrival/departure time row sits
 * below.
 */
export function StopDetail({ stop, activities }: IStopDetailProps) {
  const { t } = useTranslation();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const timeText = formatTimeRange(stop.arrivalIso, stop.departureIso, tz);
  return (
    <div className={styles.detail}>
      <header className={styles.header}>
        <span className={styles.eyebrow}>
          {t('reports.dailyRoute.location', { n: stop.stopIndex })}
        </span>
        <div className={styles.floorRow}>
          <span className={styles.floor}>{stop.floorNick}</span>
          <span className={styles.duration}>{formatDuration(stop.durationMinutes)}</span>
        </div>
        <div className={styles.subRow}>
          <span className={styles.area}>{stop.areaNick}</span>
          <span className={styles.time}>{timeText}</span>
        </div>
      </header>

      <section className={styles.section}>
        <h3 className={styles.sectionHeading}>
          {t('reports.dailyRoute.section.personnel')}
        </h3>
        <PersonnelSection personnel={stop.personnel} fallbackTimeText={timeText} />
      </section>
      <section className={styles.section}>
        <h3 className={styles.sectionHeading}>
          {t('reports.dailyRoute.section.activities')}
        </h3>
        <ActivitiesSection
          activities={activities.data ?? []}
          isLoading={activities.isLoading}
          isError={activities.isError}
          onRetry={activities.refetch}
          i18nNamespace="reports.dailyRoute"
        />
      </section>
      <section className={styles.section}>
        <h3 className={styles.sectionHeading}>
          {t('reports.dailyRoute.section.equipment')}
        </h3>
        <EquipmentSection equipment={stop.equipment} />
      </section>
    </div>
  );
}
